import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { ConsumptionAndEmissionText } from '~/app/compositions/content/consumption-and-emission-text/consumption-emission-text';
import { Gallery } from '~/app/compositions/content/gallery/gallery';
import { groupMedia } from '~/app/compositions/content/gallery/gallery-utils';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { SummaryHeaderPriceBar } from '~/app/pages/summary/components/summary-header-price-bar';
import { AudiCodeDialog } from '~/app/pages/summary/dialogs/audi-code/audi-code.dialog';
import { StandardEquipmentDialog } from '~/app/pages/summary/dialogs/standard-equipment/standard-equipment.dialog';
import { TechnicalDataDialog } from '~/app/pages/summary/dialogs/technical-data.dialog';
import { UnauthenticatedDialog } from '~/app/pages/summary/dialogs/unauthenticated.dialog';
import { MainStyle, PaddingContainer } from '~/app/pages/summary/styles/summary-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { useImages } from '~/graphql/hooks/use-images';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { state } from '~/storage';
import { NextBestActions } from './components/next-best-actions';

export default function SummaryPage() {
	const { market } = useMarketConfig();
	const marketUIConfig = useMarketUiConfig();
	const setIsBodyNoneScroll = useSetAtom(state.isBodyNoneScroll);
	const { isMobile, isMobileView, isMBreakpoint } = useBreakPoint();
	const { isLoading } = useConfigurator();
	const { mediaByProfileId } = useImages();

	// Workaround
	useEffect(() => {
		// scroll top top on mount

		setIsBodyNoneScroll(false);
		setTimeout(() => window.scrollTo(0, 0), 150);
		setTimeout(() => window?.triggerUserlane(market, 'summary'), 3000);
	}, [market, setIsBodyNoneScroll]);

	const galleryType = isMobile || isMobileView ? 'mobile' : 'onlySingleImg';
	const galleryAspectRatio = isMBreakpoint ? '4 / 3' : '16 / 9';
	const galleryImages = [
		ImageViewsEnum.EXTERIOR_FRONT_3_4,
		ImageViewsEnum.EXTERIOR_FRONT_FRONT,
		ImageViewsEnum.EXTERIOR_PARALLEL_SIDE,
		ImageViewsEnum.EXTERIOR_REAR_REAR,
		ImageViewsEnum.EXTERIOR_REAR_3_4,
		ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS,
		ImageViewsEnum.EXTERIOR_WHEELS_RIM,
		ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD,
		ImageViewsEnum.INTERIOR_SEATS_SEATS,
		ImageViewsEnum.INTERIOR_REAR_REARSEATS,
		ImageViewsEnum.EXTERIOR_REAR_TRUNK,
	];

	useEffect(() => {
		requestAnimationFrame(() => {
			const el = document.getElementById('summary-price-bar');
			if (el) {
				el.focus();
			}
		});
	}, []);

	return (
		<>
			<DialogRenderer />
			<SummaryHeaderPriceBar id={'summary-price-bar'} />
			<MainStyle id="summary-page" className="mx-auto w-full flex-1 gap-16 bg-white">
				<PaddingContainer $noPadding={isMBreakpoint}>
					<div className={'gallery-wrapper'}>
						{!isLoading ? (
							<Gallery
								id={'summary'}
								controls={{ avp: true, fullscreen: true }}
								media={groupMedia(
									galleryImages.map((img) => [mediaByProfileId[img]]),
									galleryType,
								)}
								galleryAspectRatio={galleryAspectRatio}
								aspectRatio={galleryAspectRatio}
							/>
						) : (
							<Skeleton style={{ aspectRatio: galleryAspectRatio, width: '100%' }} />
						)}

						<ConsumptionAndEmissionText containerClass={isMBreakpoint ? 'paddings' : 'vertical-padding'} />
					</div>
				</PaddingContainer>
				<PaddingContainer>
					<NextBestActions />
					{marketUIConfig.configOverviewComponent}
				</PaddingContainer>
			</MainStyle>
		</>
	);
}

function DialogRenderer() {
	const { isDialogOpen, closeDialog } = useDialogs();

	const onDialogClose = useCallback((componentName: string) => {
		appTrackingService.summaryClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: `configurator - click to close layer`,
			componentName,
		});
		appTrackingService.viewChangeEvent({
			eventName: 'generic',
		});
	}, []);

	return (
		<>
			<TechnicalDataDialog
				open={isDialogOpen('technicalData')}
				onClose={() => {
					closeDialog('technicalData');
					onDialogClose('technical-data-layer');
				}}
			/>

			<AudiCodeDialog
				open={isDialogOpen('audiCode')}
				onClose={() => {
					closeDialog('audiCode');
					onDialogClose('save-configuration-layer');
				}}
			/>

			<StandardEquipmentDialog
				open={isDialogOpen('standardEquipment')}
				onClose={() => {
					closeDialog('standardEquipment');
					onDialogClose('standard-equipment');
				}}
			/>
			<UnauthenticatedDialog
				open={isDialogOpen('unauthenticated')}
				onClose={() => closeDialog('unauthenticated')}
			/>
		</>
	);
}
